import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useApolloClient, useMutation } from "@apollo/client";

import { Button, Table, PageHeader, Card, FilterValues, QuickFilterTypes } from "@cofano/cck";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";

import DocumentDrawer from "components/documents/DocumentDrawer";
import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, DataFormat, hasPluginPermission, usePermission } from "imports/Utils";
import { RelationLink } from "utils/LinkUtils";

import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import { isInvoiceLocked } from "view/invoices/utils/InvoiceUtils";
import StatusChanger from "view/statuses/components/changer/StatusChanger";

import { isEditInvoiceAllowed } from "./utils/InvoiceUtils";

import { SearchInvoicesDocument } from "__generated__/view/invoices/graphql/invoices.queries.generated";
import { DeleteInvoiceDocument } from "__generated__/view/invoices/graphql/invoices.mutations.generated";
import { CharterType, StatusType } from "__generated__/enums";

const Invoices = ({ charterType }: { charterType: CharterType }) => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const session = useStoreState(store => store.session);

	const { isReadOnly } = usePermission();

	const usingPaymentTerms = hasPluginPermission(session, "paymentTerms");

	const [ previewInvoiceId, setPreviewInvoiceId ] = useState<number | null>(null);
	const client = useApolloClient();

	const { tableProps, refetch, refetchAfterDelete, filterValuesProps } = useBackendTable({
		id: TableId.Invoices,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: SearchInvoicesDocument,
					variables: {
						pagination: { ...pagination, sortList: sort },
						search: {
							charterType,
							invoiceNumbers: filter.invoiceNumber ? [ filter.invoiceNumber ] : undefined,
							creditors: filter.creditor?.map((o: RelationSelectOption) => o.id),
							debtors: filter.debtor?.map((o: RelationSelectOption) => o.id),
							statuses: filter.status?.id ? [ filter.status.id ] : undefined,
						},
					},
				});

				callback({
					content: result.data.searchInvoices.content,
					totalResults: result.data.searchInvoices.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deleteInvoice ] = useMutation(DeleteInvoiceDocument, {
		onCompleted: () => refetchAfterDelete(),
	});

	useEffect(() => {
		refetch();
	}, [ charterType ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("INVOICES")}
					<Helmet title={intl.formatMessage({ id: "INVOICES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("new")}>
							{Language("CREATE_INVOICE")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "invoiceNumber",
								title: intl.formatMessage({ id: "INVOICE_NUMBER" }),
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => {
						const controlPressed = event.ctrlKey;
						const url = `/view/invoices/${row.id}/details`;

						if (controlPressed) {
							window.open(url, "_blank");
						} else {
							navigate(url);
						}
					}}
					columns={[ {
						id: "invoiceNumber",
						title: intl.formatMessage({ id: "INVOICE_NUMBER" }),
						width: 150,
						filterComponent: QuickFilterTypes.TEXT,
						sortable: true,
					}, {
						id: "creditor",
						title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
						render: (row) => <RelationLink relation={row.creditor} />,
						sortable: true,
						width: 200,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
						render: (row) => <RelationLink relation={row.debtor} />,
						sortable: true,
						width: 200,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "paymentTerm",
						title: intl.formatMessage({ id: "PAYMENT_TERM" }),
						render: (row) => row.paymentTerm
							? `${row.paymentTerm.externalReference ?? ""} (${row.paymentTerm.days} ${intl.formatMessage({ id: "DAYS" })})`
							: "",
						width: 150,
						disabled: !usingPaymentTerms,
					}, {
						id: "date",
						title: intl.formatMessage({ id: "INVOICE_DATE" }),
						render: (row) => row.date ? moment(row.date).format("ll") : "",
						sortable: true,
					}, {
						id: "totalAmount",
						title: intl.formatMessage({ id: "TOTAL_INCLUDE_VAT" }),
						render: (row) => DataFormat.price(row.totalAmount ?? 0, row.currency?.isoCode || undefined, session.locale),
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						width: 150,
						render: (row) => (
							<StatusChanger
								statusType={StatusType.Invoice}
								entity={row}
								refetch={refetch}
								showHistory />
						),
					} ]}
					rowActions={[ {
						icon: <InsertDriveFile />,
						onClick: (row) => setPreviewInvoiceId(row.id),
					}, {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/invoices/${row.id}/edit`),
						disabled: (row) => !isEditInvoiceAllowed(row, session),
					}, {
						icon: <DeleteIcon />,
						hidden: !(hasPluginPermission(session, "invoiceArchiving") && !isReadOnly),
						disabled: (row) => isInvoiceLocked(row),
						onClick: (row) => {
							confirm({
								type: "WARNING",
								title: intl.formatMessage({ id: "INVOICE_DELETE_CONFIRM" }),
							}).then(() => {
								deleteInvoice({ variables: { id: row.id } });
							});
						},
					} ]} />
			</Card>

			<DocumentDrawer
				open={previewInvoiceId !== null}
				onClose={() => setPreviewInvoiceId(null)}
				previewUrl={`invoices/preview/${previewInvoiceId}`}
				url={`invoices/export/download/${previewInvoiceId}`}
				downloadedFileName={`Invoice-${previewInvoiceId}.pdf`} />
		</div>
	);
};

export default Invoices;
